import type { MatterDTO } from "../../api/types/matter";

export type CaseChipCounts = {
  /** Every case the unfiltered list matches (the server's total). */
  all: number;
  /** Cases assigned to the signed-in user; null until every case is loaded. */
  mine: number | null;
  /** The most common visa types, most cases first; counts need every case loaded. */
  visas: Array<{ visa: string; count: number | null }>;
};

export const TOP_VISA_CHIPS = 4;

const visaOf = (m: MatterDTO): string => {
  const fromCase = m.case?.visa;
  if (typeof fromCase === "string" && fromCase.trim()) return fromCase.trim();
  const ind = (m.individual ?? {}) as Record<string, unknown>;
  return typeof ind.visa === "string" ? ind.visa.trim() : "";
};

const isAssignedTo = (m: MatterDTO, uid: string): boolean =>
  (m.assigned_to_user_ids ?? []).some((id) => (id ?? "").trim() === uid) ||
  (m.assigned_to_user_profiles ?? []).some(
    (p) => (p?.uid ?? "").trim() === uid
  );

/**
 * Quick-filter chips for the cases toolbar, from the unfiltered list. The
 * list pages through the server, so per-chip counts are only shown once
 * every case is loaded; before that the chips filter without a count.
 */
export const caseChipCounts = (
  items: MatterDTO[],
  {
    total,
    complete,
    uid
  }: { total: number; complete: boolean; uid?: string | null }
): CaseChipCounts => {
  const byVisa = new Map<string, number>();
  items.forEach((m) => {
    const visa = visaOf(m);
    if (visa) byVisa.set(visa, (byVisa.get(visa) ?? 0) + 1);
  });
  const visas = Array.from(byVisa, ([visa, count]) => ({ visa, count }))
    .sort((a, b) => b.count - a.count || a.visa.localeCompare(b.visa))
    .slice(0, TOP_VISA_CHIPS)
    .map(({ visa, count }) => ({ visa, count: complete ? count : null }));
  const me = (uid ?? "").trim();
  return {
    all: total,
    mine:
      complete && me ? items.filter((m) => isAssignedTo(m, me)).length : null,
    visas
  };
};
